import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ServiceSelectionEnum } from '../../../../../../core/enums/applications/service-selection-enum';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ApplicationService } from '../../../../shared/services/application/application.service';
import { ApplicationPageFacadeService } from '../../../shared/services/application-page-facade/application-page-facade.service';
import { SubmitToEwaFormSubmissionComponent } from '../shared/components/submit-to-ewa-form-submission/submit-to-ewa-form-submission.component';
import { SubmitToEwaPageBaseComponent } from '../shared/components/submit-to-ewa-page-base/submit-to-ewa-page-base.component';
import { EwaSubmissionResponse } from '../shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../shared/models/submit-to-ewa-details-response';
import { SubmitToEwaFormService } from '../shared/services/submit-to-ewa-form.service';
import { AdvisorIndustrialBusinessSubmitToEwaFormModel } from './models/advisor-industrial-business-submit-to-ewa-form-model';
import { AdvisorIndustrialBusinessSubmitToEwaRequest } from './models/advisor-industrial-business-submit-to-ewa-request';

@Component({
    selector: 'app-advisor-industrial-business-submit-to-ewa',
    templateUrl: './advisor-industrial-business-submit-to-ewa.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SubmitToEwaFormSubmissionComponent],
})
export class AdvisorIndustrialBusinessSubmitToEwaComponent extends SubmitToEwaPageBaseComponent<
    AdvisorIndustrialBusinessSubmitToEwaRequest,
    AdvisorIndustrialBusinessSubmitToEwaFormModel
> {
    /**
     * The default service selection for this form.
     */
    readonly serviceSelection: ServiceSelectionEnum =
        ServiceSelectionEnum.EXPRESS;

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly submitToEwaService: SubmitToEwaFormService,
        private readonly applicationService: ApplicationService,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    override form: FormGroup<
        FormGroupType<AdvisorIndustrialBusinessSubmitToEwaFormModel>
    > = new FormGroup<
        FormGroupType<AdvisorIndustrialBusinessSubmitToEwaFormModel>
    >({});

    /**
     * Submits the form and shows the submission complete dialog with
     * navigation to the dashboard.
     */
    handoverToEwaSubmission(): void {
        this.submitToEwaService.handoverToEwaSubmission(
            () => this.saveFormDataToApi(this.generateRequestObject(true)),
            () => {
                /**
                 * This is needed to prevent the guard from blocking the navigation
                 * since the submission has already been made and the user is being redirected.
                 */
                this.hasChanges = false;
            },
        );
    }

    protected override bindForm(response: SubmitToEwaDetailsResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        return this.applicationService.getDataForFinalSubmission(applicationId);
    }

    protected override saveFormDataToApi(
        request: AdvisorIndustrialBusinessSubmitToEwaRequest,
    ): Observable<EwaSubmissionResponse> {
        return this.applicationService.finalSubmissionToEwa(request);
    }

    protected override generateRequestObject(
        isFinalSubmission: boolean = false,
    ): AdvisorIndustrialBusinessSubmitToEwaRequest {
        const formValue = this.form
            .value as Required<AdvisorIndustrialBusinessSubmitToEwaRequest>;

        return {
            ...formValue,
            submitToEwa: isFinalSubmission,
            applicationId: this.applicationId!,
        };
    }
}
